'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    getBillingBatches,
    BillingBatch,
    BATCH_STATUS_LABELS,
    BATCH_STATUS_COLORS,
    formatCompetence,
} from '@/lib/api/billing/billing-batch-api'
import {
    MdInventory,
    MdSearch,
    MdClose,
    MdAdd,
    MdArrowBack,
    MdArrowForward,
    MdLocalHospital,
    MdAccessTime,
    MdDescription,
} from 'react-icons/md'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

export default function BatchesPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate } = useResourcePermissions('billing', 'batches')
    const [batches, setBatches] = useState<BillingBatch[]>([])
    const [loading, setLoading] = useState(true)
    const [showSearch, setShowSearch] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('all')

    useEffect(() => {
        if (user?.companyId) {
            loadBatches()
        }
    }, [user?.companyId])

    const loadBatches = async () => {
        setLoading(true)
        try {
            const response = await getBillingBatches()
            if (response.success) {
                setBatches(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar lotes')
            }
        } catch (error) {
            toast.error('Erro ao carregar lotes')
        } finally {
            setLoading(false)
        }
    }

    const filteredBatches = batches.filter(batch => {
        if (searchTerm) {
            const search = searchTerm.toLowerCase()
            if (
                !batch.batchNumber.toLowerCase().includes(search) &&
                !batch.healthPlan?.name?.toLowerCase().includes(search)
            ) {
                return false
            }
        }
        if (statusFilter !== 'all' && batch.status !== statusFilter) {
            return false
        }
        return true
    })

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value)
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando lotes...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/billing')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
                        <MdInventory className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Lotes de Faturamento
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {batches.length} {batches.length === 1 ? 'lote' : 'lotes'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {showSearch ? (
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                placeholder="Buscar lotes..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="input input-bordered input-sm w-64"
                                autoFocus
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    setShowSearch(false)
                                    setSearchTerm('')
                                }}
                                className="btn btn-sm btn-ghost"
                            >
                                <MdClose className="text-lg" />
                            </button>
                        </div>
                    ) : (
                        <>
                            <button
                                onClick={() => setShowSearch(true)}
                                className="btn btn-sm btn-ghost gap-2"
                            >
                                <MdSearch className="text-lg" />
                                <span className="hidden sm:inline">Buscar</span>
                            </button>
                            {canCreate && (
                                <button
                                    onClick={() => navigate('/billing/batches/new')}
                                    className="btn btn-secondary gap-2"
                                >
                                    <MdAdd className="text-lg" />
                                    <span className="hidden sm:inline">Novo Lote</span>
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Status Filter */}
            <div className="flex gap-2 flex-wrap">
                <button
                    onClick={() => setStatusFilter('all')}
                    className={`btn btn-sm ${statusFilter === 'all' ? 'btn-secondary' : 'btn-ghost'}`}
                >
                    Todos
                </button>
                <button
                    onClick={() => setStatusFilter('BATCH_OPEN')}
                    className={`btn btn-sm ${statusFilter === 'BATCH_OPEN' ? 'btn-info' : 'btn-ghost'}`}
                >
                    Abertos
                </button>
                <button
                    onClick={() => setStatusFilter('BATCH_CLOSED')}
                    className={`btn btn-sm ${statusFilter === 'BATCH_CLOSED' ? 'btn-warning' : 'btn-ghost'}`}
                >
                    Fechados
                </button>
                <button
                    onClick={() => setStatusFilter('BATCH_SUBMITTED')}
                    className={`btn btn-sm ${statusFilter === 'BATCH_SUBMITTED' ? 'btn-info' : 'btn-ghost'}`}
                >
                    Enviados
                </button>
                <button
                    onClick={() => setStatusFilter('BATCH_PROCESSED')}
                    className={`btn btn-sm ${statusFilter === 'BATCH_PROCESSED' ? 'btn-success' : 'btn-ghost'}`}
                >
                    Processados
                </button>
                <button
                    onClick={() => setStatusFilter('BATCH_COMPLETED')}
                    className={`btn btn-sm ${statusFilter === 'BATCH_COMPLETED' ? 'btn-success' : 'btn-ghost'}`}
                >
                    Concluídos
                </button>
            </div>

            {filteredBatches.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-12 text-center">
                    <MdInventory className="text-6xl text-base-content/20 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-base-content mb-2">
                        Nenhum lote encontrado...
                    </h3>
                    <p className="text-base-content/60 mb-6">
                        Crie seu primeiro lote de faturamento
                    </p>
                    {canCreate && (
                        <button
                            onClick={() => navigate('/billing/batches/new')}
                            className="btn btn-secondary gap-2"
                        >
                            <MdAdd className="text-lg" />
                            Novo Lote
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredBatches.map(batch => (
                        <div
                            key={batch.id}
                            onClick={() => navigate(`/billing/batches/${batch.id}`)}
                            className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-5 hover:shadow-md transition-shadow cursor-pointer"
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span
                                            className={`badge badge-sm ${BATCH_STATUS_COLORS[batch.status]}`}
                                        >
                                            {BATCH_STATUS_LABELS[batch.status]}
                                        </span>
                                    </div>
                                    <h3 className="font-semibold text-base-content mb-1">
                                        {batch.batchNumber}
                                    </h3>
                                    <p className="text-sm text-base-content/60">
                                        {formatCompetence(
                                            batch.competenceMonth,
                                            batch.competenceYear
                                        )}
                                    </p>
                                </div>
                                <MdArrowForward className="text-base-content/40 flex-shrink-0 ml-2" />
                            </div>

                            <div className="space-y-2 mb-3">
                                {batch.healthPlan && (
                                    <div className="flex items-center gap-2 text-sm text-base-content/70">
                                        <MdLocalHospital className="text-base" />
                                        <span className="truncate">
                                            {batch.healthPlan.name}
                                        </span>
                                    </div>
                                )}
                                <div className="flex items-center gap-2 text-sm text-base-content/60">
                                    <MdDescription className="text-base" />
                                    <span>
                                        {batch.totalGuides}{' '}
                                        {batch.totalGuides === 1 ? 'guia' : 'guias'}
                                    </span>
                                </div>
                            </div>

                            <div className="flex items-center justify-between pt-3 border-t border-base-300/50">
                                <div className="flex items-center gap-2 text-xs text-base-content/60">
                                    <MdAccessTime className="text-sm" />
                                    <span>
                                        {format(
                                            new Date(batch.createdAt),
                                            'dd/MM/yyyy',
                                            { locale: ptBR }
                                        )}
                                    </span>
                                </div>
                                <span className="text-sm font-semibold text-secondary">
                                    {formatCurrency(batch.totalValue)}
                                </span>
                            </div>

                            {batch.totalGloss && batch.totalGloss > 0 && (
                                <div className="mt-2 flex items-center justify-between text-xs">
                                    <span className="text-error">
                                        Glosas: {formatCurrency(batch.totalGloss)}
                                    </span>
                                    {batch.totalPaid && (
                                        <span className="text-success">
                                            Pago: {formatCurrency(batch.totalPaid)}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
